import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send } from 'lucide-react';
interface ContactModalProps {
  open: boolean;
  onClose: () => void;
}
export function ContactModal({
  open,
  onClose
}: ContactModalProps) {
  const [from, setFrom] = useState('');
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);
  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);
  const handleSend = (e: React.FormEvent) => {
    e.preventDefault();
    if (!from || !message) return;
    setSent(true);
    setTimeout(() => {
      setSent(false);
      setFrom('');
      setMessage('');
      onClose();
    }, 1800);
  };
  const panelVariants = {
    // Mobile: slide up from bottom
    mobileHidden: {
      y: '100%',
      opacity: 1
    },
    mobileVisible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.38,
        ease: [0.32, 0.72, 0, 1]
      }
    },
    mobileExit: {
      y: '100%',
      opacity: 1,
      transition: {
        duration: 0.28,
        ease: [0.32, 0.72, 0, 1]
      }
    },
    // Desktop: slide in from left
    desktopHidden: {
      x: '-100%',
      opacity: 1
    },
    desktopVisible: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.38,
        ease: [0.32, 0.72, 0, 1]
      }
    },
    desktopExit: {
      x: '-100%',
      opacity: 1,
      transition: {
        duration: 0.28,
        ease: [0.32, 0.72, 0, 1]
      }
    }
  };
  return <AnimatePresence data-id="element-47">
      {open && <>
          {/* Backdrop */}
          <motion.div key="backdrop" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} onClick={onClose} className="fixed inset-0 bg-black/20 backdrop-blur-[2px] z-[60]" data-id="element-48" />

          {/* Mobile panel — slides up from bottom */}
          <motion.div key="mobile-panel" initial="mobileHidden" animate="mobileVisible" exit="mobileExit" variants={panelVariants} className="md:hidden fixed bottom-0 left-0 right-0 z-[70] bg-[#F8F6F3] rounded-t-2xl shadow-2xl" data-id="element-49">
            <PanelContent from={from} setFrom={setFrom} message={message} setMessage={setMessage} sent={sent} onSend={handleSend} onClose={onClose} data-id="element-50" />
          </motion.div>

          {/* Desktop panel — slides in from left, sits over sidebar */}
          <motion.div key="desktop-panel" initial="desktopHidden" animate="desktopVisible" exit="desktopExit" variants={panelVariants} className="hidden md:flex fixed left-0 top-0 h-screen w-80 lg:w-96 z-[70] bg-[#F8F6F3] shadow-2xl border-r border-[#E8E4DF] flex-col" data-id="element-51">
            <PanelContent from={from} setFrom={setFrom} message={message} setMessage={setMessage} sent={sent} onSend={handleSend} onClose={onClose} data-id="element-52" />
          </motion.div>
        </>}
    </AnimatePresence>;
}
function PanelContent({
  from,
  setFrom,
  message,
  setMessage,
  sent,
  onSend,
  onClose
}: {
  from: string;
  setFrom: (v: string) => void;
  message: string;
  setMessage: (v: string) => void;
  sent: boolean;
  onSend: (e: React.FormEvent) => void;
  onClose: () => void;
}) {
  return <div className="flex flex-col h-full" data-id="element-53">
      {/* Header */}
      <div className="flex items-center justify-between px-7 pt-7 pb-5 border-b border-[#E8E4DF] shrink-0" data-id="element-54">
        <h2 className="text-base font-bold text-[#1A1A1A] tracking-tight" data-id="element-55">
          New Message
        </h2>
        <button onClick={onClose} className="w-7 h-7 flex items-center justify-center text-[#A09A94] hover:text-[#1A1A1A] transition-colors rounded-full hover:bg-[#E8E4DF]" aria-label="Close" data-id="element-56">
          <X size={15} data-id="element-57" />
        </button>
      </div>

      {/* Form */}
      <form onSubmit={onSend} className="flex flex-col flex-1 overflow-hidden" data-id="element-58">
        <div className="flex-1 overflow-y-auto" data-id="element-59">
          {/* To field */}
          <div className="flex items-center gap-3 px-7 py-4 border-b border-[#E8E4DF]" data-id="element-60">
            <span className="text-xs font-semibold uppercase tracking-widest text-[#A09A94] w-12 shrink-0" data-id="element-61">
              To
            </span>
            <span className="text-sm text-[#1A1A1A] font-medium" data-id="element-62">
              jp.davidson@icloud.com
            </span>
          </div>

          {/* From field */}
          <div className="flex items-center gap-3 px-7 py-4 border-b border-[#E8E4DF]" data-id="element-63">
            <label htmlFor="contact-from" className="text-xs font-semibold uppercase tracking-widest text-[#A09A94] w-12 shrink-0" data-id="element-64">
              From
            </label>
            <input id="contact-from" type="email" value={from} onChange={e => setFrom(e.target.value)} placeholder="your@email.com" required className="flex-1 bg-transparent text-sm text-[#1A1A1A] placeholder:text-[#C8C2BB] outline-none" data-id="element-65" />
          </div>

          {/* Message field */}
          <div className="flex flex-col gap-2 px-7 py-5" data-id="element-66">
            <label htmlFor="contact-message" className="text-xs font-semibold uppercase tracking-widest text-[#A09A94]" data-id="element-67">
              Message
            </label>
            <textarea id="contact-message" value={message} onChange={e => setMessage(e.target.value)} placeholder="What can I help with?" required rows={6} className="w-full bg-transparent text-sm text-[#1A1A1A] placeholder:text-[#C8C2BB] outline-none resize-none leading-relaxed" data-id="element-68" />
          </div>
        </div>

        {/* Send button */}
        <div className="px-7 pb-8 pt-4 shrink-0 border-t border-[#E8E4DF]" data-id="element-69">
          <button type="submit" disabled={sent} className="w-full flex items-center justify-center gap-2.5 bg-[#3A6B4A] text-white text-sm font-medium py-3.5 rounded-xl hover:bg-[#2F5A3D] active:scale-[0.98] transition-all duration-200 disabled:opacity-60" data-id="element-70">
            {sent ? <motion.span initial={{
            opacity: 0,
            y: 4
          }} animate={{
            opacity: 1,
            y: 0
          }} className="flex items-center gap-2" data-id="element-71">
                ✓ Sent
              </motion.span> : <>
                <Send size={14} data-id="element-72" />
                Get in touch
              </>}
          </button>
        </div>
      </form>
    </div>;
}